import React, { useState, useEffect } from "react";
import ContentLoader from "react-content-loader";
import { toast, ToastContainer } from "react-toastify";
import { Building2, Plus } from "lucide-react";
import Tooltip from "@tippyjs/react";
import MaterialTable from 'material-table';
import "tippy.js/dist/tippy.css";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { getTrans as t } from "../../Localization/translations";
import lineOfBusinessService from "../../services/company/lineofbusinessService";

function LinesOfBusiness() {
  const lobTableRef = React.createRef();
  const [linesOfBusiness, setLinesOfBusiness] = useState([]);
  const [newLineOfBusiness, setNewLineOfBusiness] = useState({ lob_code: "", lob_name: "", is_default: false });
  const [editingLineOfBusiness, setEditingLineOfBusiness] = useState(null);
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLinesOfBusiness();
  }, []);

  const fetchLinesOfBusiness = () => {
    setIsLoading(true);
    lineOfBusinessService.getLineOfBusiness().then((response) => {
      setLinesOfBusiness(response.data);
      setIsLoading(false);
    }).catch(err => {
      toast.error(t("Failed to fetch lines of business"), { position: "top-right", autoClose: 2000, theme: "light" });
      setIsLoading(false);
    });
  };

  const addLineOfBusiness = async () => {
    if (!newLineOfBusiness.lob_code.trim() || !newLineOfBusiness.lob_name.trim()) {
      setError(t("Code and name are required"));
      return;
    }
    setIsLoading(true);
    try {
      const response = await lineOfBusinessService.createLineOfBusiness(newLineOfBusiness);
      setLinesOfBusiness([...linesOfBusiness, response.data]);
      setNewLineOfBusiness({ lob_code: "", lob_name: "", is_default: false });
      setIsAddFormOpen(false);
      setError(null);
      setIsLoading(false);
      toast.success(t("Added successfully"), { position: "top-right", autoClose: 2000, theme: "light" });
    } catch (err) {
      setError(t("Failed to add"));
      setIsLoading(false);
      toast.error(t("Failed to add"), { position: "top-right", autoClose: 2000, theme: "light" });
    }
  };

  const editLineOfBusiness = (lob) => {
    setEditingLineOfBusiness(lob);
    setIsEditFormOpen(true);
    setIsAddFormOpen(false);
  };

  const saveEditedLineOfBusiness = async () => {
    if (!editingLineOfBusiness?.lob_code.trim() || !editingLineOfBusiness?.lob_name.trim()) {
      setError(t("Code and name are required"));
      return;
    }
    setIsLoading(true);
    try {
      const response = await lineOfBusinessService.updateLineOfBusiness(editingLineOfBusiness.id, editingLineOfBusiness);
      setLinesOfBusiness(linesOfBusiness.map((lob) => lob.id === editingLineOfBusiness.id ? response.data : lob));
      setIsEditFormOpen(false);
      setEditingLineOfBusiness(null);
      setError(null);
      setIsLoading(false);
      toast.success(t("Updated successfully"), { position: "top-right", autoClose: 2000, theme: "light" });
    } catch (err) {
      setError(t("Failed to update"));
      setIsLoading(false);
      toast.error(t("Failed to update"), { position: "top-right", autoClose: 2000, theme: "light" });
    }
  };

  const removeLineOfBusiness = (id) => {
    confirmAlert({
      title: '',
      message: 'Are you sure you want to delete this Line of Business?',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            setIsLoading(true);

            try {
              await lineOfBusinessService.deleteLineOfBusiness(id);

              // Optimistic UI update
              setLinesOfBusiness(prev =>
                prev.filter(lob => lob.id !== id)
              );

              toast.success(t("Deleted successfully"), {
                position: "top-right",
                autoClose: 2000,
                theme: "light",
              });

            } catch (err) {
              console.error("LOB delete failed:", err);

              toast.error(t("Failed to delete"), {
                position: "top-right",
                autoClose: 2000,
                theme: "light",
              });

            } finally {
              setIsLoading(false);
            }
          },
        },
        {
          label: 'No',
          onClick: () => { },
        },
      ],
    });
  };


  return (
    <div className="container-fluid mt-3">
      <ToastContainer />
      <div className="card mt-3">
        <div className="card-header d-flex justify-content-between align-items-center p-3">
          <div>
            <div className="text-lg flex items-center gap-2 text-dark">
              <Building2 className="w-5 h-5 mr-2" />
              {t("Lines of Business")}
            </div>
            <p className="text-sm text-gray-600">{t("Manage your lines of business")}</p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              setIsAddFormOpen(!isAddFormOpen);
              setIsEditFormOpen(false);
              setNewLineOfBusiness({ lob_code: "", lob_name: "", is_default: false });
              setEditingLineOfBusiness(null);
              setError(null);
            }}
            disabled={isLoading}
          >
            <Plus className="w-11 h-11 mr-2" />
            {t("Add Line of Business")}
          </button>
        </div>
        <div className="card-body p-3">
          {isLoading && !linesOfBusiness.length ? (
            <ContentLoader viewBox="0 0 400 100" backgroundColor="rgba(0,0,0,0.06)" foregroundColor="rgba(0,0,0,0.12)">
              <rect x="0" y="10" rx="4" ry="4" width="400" height="20" />
              <rect x="0" y="40" rx="4" ry="4" width="400" height="20" />
              <rect x="0" y="70" rx="4" ry="4" width="400" height="20" />
            </ContentLoader>
          ) : (
            <>
              <MaterialTable
                tableRef={lobTableRef}
                style={{ backgroundColor: "var(--bs-table-bg)", borderBottomWidth: "1px", boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)" }}
                title={<h5 className="mb-0">{t("Lines of Business")}</h5>}
                columns={[
                  { title: t("Business Name"), field: "lob_name", render: rowData => (<div>{rowData.lob_name}</div>), headerStyle: { fontWeight: "bold" } },
                  { title: t("Business Code"), field: "lob_code", render: rowData => (<div>{rowData.lob_code}</div>), headerStyle: { fontWeight: "bold" } },
                  { title: t("Default"), field: "is_default", render: rowData => (<div>{rowData.is_default ? 'Yes' : 'No'}</div>), headerStyle: { fontWeight: "bold" } },
                ]}
                data={linesOfBusiness}
                actions={[
                  { icon: () => <img src={process.env.PUBLIC_URL + "/static/images/ic-edit.svg"} alt="edit" />, tooltip: t("Edit"), onClick: (event, rowData) => editLineOfBusiness(rowData) },
                  { icon: () => <img src={process.env.PUBLIC_URL + "/static/images/ic-delete.svg"} alt="delete" />, tooltip: t("Delete"), onClick: (event, rowData) => removeLineOfBusiness(rowData.id) },
                ]}
                options={{ headerStyle: { backgroundColor: "white", color: "#245C7F", fontSize: 11 }, rowStyle: (rowData, index) => ({ backgroundColor: index % 2 ? "#fff" : undefined }), paging: false, pageSize: 10, actionsColumnIndex: -1, toolbar: false }}
              />

              {(isAddFormOpen || isEditFormOpen) && (
                <div className="mt-5">
                  <h4 className="text-md">{isAddFormOpen ? t("Add New Line of Business") : t("Edit Line of Business")}</h4>
                  <div>{isAddFormOpen ? t("Enter the name for the new line of business.") : t("Update the line of business name.")}</div>
                  <div className="row mt-3">
                    <div className="col-md-4">
                      <label htmlFor="lob-code" className="col-form-label">
                        {t("Business Code")} <Tooltip content={t("Businesscode_Tooltip")}><i className="fa fa-light fa-circle-info"></i></Tooltip>
                      </label>
                      <input id="lob-code" className="form-control w-full border rounded-md p-2" value={isAddFormOpen ? newLineOfBusiness.lob_code : editingLineOfBusiness?.lob_code} onChange={(e) => isAddFormOpen ? setNewLineOfBusiness({ ...newLineOfBusiness, lob_code: e.target.value }) : setEditingLineOfBusiness({ ...editingLineOfBusiness, lob_code: e.target.value })} placeholder={t("Enter line of business code")} />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="lob-name" className="col-form-label">
                        {t("Business Name")} <Tooltip content={t("Businessname_Tooltip")}><i className="fa fa-light fa-circle-info"></i></Tooltip>
                      </label>
                      <input id="lob-name" className="form-control w-full border rounded-md p-2" value={isAddFormOpen ? newLineOfBusiness.lob_name : editingLineOfBusiness?.lob_name} onChange={(e) => isAddFormOpen ? setNewLineOfBusiness({ ...newLineOfBusiness, lob_name: e.target.value }) : setEditingLineOfBusiness({ ...editingLineOfBusiness, lob_name: e.target.value })} placeholder={t("Enter line of business name")} />
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                      <div className="w-100">
                        <label htmlFor="lob-default" className="col-form-label">
                          <input id="lob-default" type="checkbox" checked={isAddFormOpen ? newLineOfBusiness.is_default : editingLineOfBusiness?.is_default} onChange={(e) => isAddFormOpen ? setNewLineOfBusiness({ ...newLineOfBusiness, is_default: e.target.checked }) : setEditingLineOfBusiness({ ...editingLineOfBusiness, is_default: e.target.checked })} />
                          <span className="ml-2">{t("Set as Default")} <Tooltip content={t("setasdefault_Tooltip")}><i className="fa fa-light fa-circle-info"></i></Tooltip></span>
                        </label>
                      </div>
                    </div>
                  </div>
                  {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
                  <div className="mt-3">
                    <button className="btn btn-secondary" onClick={() => { setIsAddFormOpen(false); setIsEditFormOpen(false); }}>{t("Cancel")}</button>
                    <button className="ml-2 btn btn-primary" onClick={isAddFormOpen ? addLineOfBusiness : saveEditedLineOfBusiness}>{isLoading ? "Saving..." : (isAddFormOpen ? t("Save") : t("Update"))}</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LinesOfBusiness;